// src/components/MessagePanel.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { readToken } from './AccountPanel';

/**
 * Direct messages.
 *
 * TWO VIEWS, NOT THREE. A list of conversations, and one conversation. Opening
 * someone replaces the list rather than sitting beside it, because this panel is
 * a column roughly 460px wide — a list-plus-thread split at that width gives
 * neither half enough room to read.
 *
 * The panel never decides who may be written to. `canSend` and `blockedReason`
 * come back with the thread, and the composer renders whichever it is told. That
 * keeps one privacy rule, on the server, instead of two that can disagree.
 *
 * Bodies are plain text and are rendered as text. Nothing here sets innerHTML.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  dim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent)',
};

const POLL_MS = 15000;

export default function MessagePanel({ apiBase, openWith }) {
  const [signedIn, setSignedIn] = useState(() => !!readToken());
  const [threads, setThreads] = useState([]);
  const [openId, setOpenId] = useState(openWith || null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [privacy, setPrivacy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const call = useCallback(async (path, opts = {}) => {
    const tok = readToken();
    const res = await fetch(`${apiBase}/api/messages/${path}`, {
      ...opts,
      headers: {
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      },
    });
    const d = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, d };
  }, [apiBase]);

  useEffect(() => {
    const onAuth = (e) => setSignedIn(!!e.detail?.signedIn);
    window.addEventListener('decon:auth', onAuth);
    return () => window.removeEventListener('decon:auth', onAuth);
  }, []);

  // my own privacy setting, read from the account rather than assumed
  useEffect(() => {
    if (!signedIn) return;
    const tok = readToken();
    fetch(`${apiBase}/api/account/me`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.ok) setPrivacy(d.user.messagePrivacy || 'connections'); })
      .catch(() => {});
  }, [apiBase, signedIn]);

  const loadThreads = useCallback(async () => {
    const { ok, d, status } = await call('threads');
    if (status === 401) { setSignedIn(false); return; }
    if (ok) setThreads(d.threads || []);
  }, [call]);

  const loadThread = useCallback(async (userId) => {
    const { ok, d } = await call(`with/${userId}`);
    if (ok) setThread(d);
  }, [call]);

  // Arriving from a Network card while this panel is already mounted: openWith
  // changes but state initialised from it would not follow, so the panel would
  // sit on whatever thread was open before.
  useEffect(() => { if (openWith) setOpenId(openWith); }, [openWith]);

  useEffect(() => { if (signedIn) loadThreads(); }, [signedIn, loadThreads]);
  useEffect(() => { if (signedIn && openId) loadThread(openId); }, [signedIn, openId, loadThread]);

  // There is no push channel yet, so new messages arrive on a poll. Slow on
  // purpose: this is a design tool people leave open all day, and a chat-speed
  // poll would be a request every second or two for the whole session.
  useEffect(() => {
    if (!signedIn) return undefined;
    const t = setInterval(() => {
      loadThreads();
      if (openId) loadThread(openId);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [signedIn, openId, loadThreads, loadThread]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [thread?.messages?.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !openId) return;
    setBusy(true); setNotice(null);
    const { ok, d } = await call('send', {
      method: 'POST', body: JSON.stringify({ userId: openId, body }),
    });
    setBusy(false);
    if (!ok) { setNotice(d.error || 'Could not send that.'); return; }
    setDraft('');
    loadThread(openId);
    loadThreads();
  };

  /**
   * Paint the choice immediately, then confirm it.
   *
   * Waiting for the round trip before highlighting the chip made the toggle feel
   * broken: on a forwarded port that is a few hundred milliseconds of the click
   * apparently doing nothing, so people click again. This is a two-value setting
   * the server almost always accepts, which is exactly the case where showing
   * the result first is safe — and if it does fail, the chip snaps back and says
   * why rather than quietly lying about what is stored.
   */
  const setMyPrivacy = async (want) => {
    if (want === privacy) return;
    const previous = privacy;
    setPrivacy(want);
    setNotice(null);
    const { ok, d } = await call('privacy', {
      method: 'PATCH', body: JSON.stringify({ messagePrivacy: want }),
    });
    if (!ok) {
      setPrivacy(previous);
      setNotice(d.error || 'Could not change that setting.');
      return;
    }
    // trust the server's answer over the guess, in case it stored something else
    if (d.messagePrivacy !== want) setPrivacy(d.messagePrivacy);
  };

  if (!signedIn) {
    return (
      <div style={S.wrap}>
        <div style={S.empty}>
          <div style={S.emptyHead}>Sign in to see your messages</div>
          <div style={S.hint}>Messages are between named accounts, so there is nothing to show yet.</div>
        </div>
      </div>
    );
  }

  // ---- one conversation ---------------------------------------------------
  if (openId && thread) {
    return (
      <div style={S.wrap}>
        <div style={S.threadHead}>
          <button style={S.back} onClick={() => { setOpenId(null); setThread(null); loadThreads(); }}>‹ All</button>
          <div>
            <div style={S.name}>{thread.person.displayName}</div>
            <div style={S.sub}>
              {thread.person.firmName || ''}{thread.person.city ? ` · ${thread.person.city}` : ''}
            </div>
          </div>
        </div>

        <div style={S.log}>
          {!thread.messages.length && (
            <div style={S.hint}>No messages yet. What you write first sets the tone — say which project you mean.</div>
          )}
          {thread.messages.map((m) => (
            <div key={m.id} style={{ ...S.bubble, ...(m.mine ? S.mine : S.theirs) }}>
              <div style={S.body}>{m.body}</div>
              <div style={S.stamp}>{shortTime(m.at)}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {notice && <div style={S.notice} onClick={() => setNotice(null)}>{notice}</div>}

        {thread.canSend ? (
          <div style={S.composer}>
            <textarea
              style={S.textarea} rows={2} value={draft} placeholder="Write a message…"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — the convention
                // everyone already has in their fingers.
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
            />
            <button style={S.primary} disabled={busy || !draft.trim()} onClick={send}>Send</button>
          </div>
        ) : (
          <div style={S.blocked}>{thread.blockedReason}</div>
        )}
      </div>
    );
  }

  // ---- the list -----------------------------------------------------------
  return (
    <div style={S.wrap}>
      {/* the privacy toggle can fail too, and this view had nowhere to say so */}
      {notice && <div style={S.notice} onClick={() => setNotice(null)}>{notice}</div>}

      <div style={S.privacyRow}>
        <span style={S.sub}>Who can start a conversation with you</span>
        <div style={S.row}>
          {[['connections', 'My connections'], ['anyone', 'Anyone']].map(([k, label]) => (
            <button key={k} style={{ ...S.chip, ...(privacy === k ? S.chipOn : null) }}
                    onClick={() => setMyPrivacy(k)}>{label}</button>
          ))}
        </div>
        <div style={S.hint}>
          {privacy === 'anyone'
            ? 'Anyone registered can write to you. Switch back if that becomes noisy — conversations you already have will continue either way.'
            : 'Only people you are connected to can open a conversation. Anyone you have already replied to can still reach you.'}
        </div>
      </div>

      {!threads.length && (
        <div style={S.hint}>
          No conversations yet. Find someone under Network — a connection is the
          usual way in, and their contact details appear there once they accept.
        </div>
      )}

      {threads.map((t) => (
        <button key={t.userId} style={S.threadRow} onClick={() => setOpenId(t.userId)}>
          <div style={S.threadTop}>
            <span style={S.name}>{t.person.displayName}</span>
            {t.unread > 0 && <span style={S.badge}>{t.unread}</span>}
          </div>
          <div style={S.preview}>
            {t.lastMessage?.mine ? 'You: ' : ''}{t.lastMessage?.body}
          </div>
          <div style={S.sub}>{shortTime(t.lastMessage?.at)}</div>
        </button>
      ))}
    </div>
  );
}

function shortTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 9, padding: 14, maxWidth: 460 },
  row: { display: 'flex', gap: 6 },
  privacyRow: { display: 'flex', flexDirection: 'column', gap: 6, padding: 10,
    border: `1px solid ${T.border}`, borderRadius: 9 },
  chip: { fontSize: 11, padding: '5px 10px', borderRadius: 14, cursor: 'pointer',
    background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)', border: '1px solid var(--btn-edge,#56657a)' },
  chipOn: { border: `1px solid ${T.accent}`, background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: T.text },
  threadRow: { display: 'flex', flexDirection: 'column', gap: 3, padding: 10, textAlign: 'left',
    border: `1px solid ${T.border}`, borderRadius: 9, background: T.surface, cursor: 'pointer' },
  threadTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  name: { fontSize: 13.5, fontWeight: 600, color: T.text },
  sub: { fontSize: 10.5, color: T.dim },
  preview: { fontSize: 11.5, color: T.dim, overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' },
  badge: { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 9,
    background: T.accent, color: '#fff' },
  threadHead: { display: 'flex', alignItems: 'center', gap: 10 },
  back: { fontSize: 12, fontWeight: 600, padding: '5px 9px', borderRadius: 7, cursor: 'pointer',
    background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)', border: '1px solid var(--btn-edge,#56657a)' },
  log: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto',
    padding: '4px 2px' },
  bubble: { maxWidth: '82%', padding: '7px 10px', borderRadius: 10, display: 'flex',
    flexDirection: 'column', gap: 2 },
  mine: { alignSelf: 'flex-end', background: 'color-mix(in srgb, var(--accent) 22%, transparent)',
    border: `1px solid ${T.accent}` },
  theirs: { alignSelf: 'flex-start', background: T.surface, border: `1px solid ${T.border}` },
  body: { fontSize: 12.5, color: T.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  stamp: { fontSize: 9.5, color: T.dim, alignSelf: 'flex-end' },
  composer: { display: 'flex', gap: 6, alignItems: 'flex-end' },
  textarea: { flex: 1, fontSize: 12.5, padding: '7px 9px', borderRadius: 7, resize: 'vertical',
    background: T.surface, color: T.text, border: `1px solid ${T.border}`, fontFamily: 'inherit' },
  primary: { fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 7,
    background: T.accent, color: '#fff', border: 'none', cursor: 'pointer' },
  blocked: { fontSize: 11.5, color: T.dim, lineHeight: 1.5, padding: 10,
    border: `1px dashed ${T.border}`, borderRadius: 9 },
  notice: { fontSize: 11.5, color: T.text, background: 'color-mix(in srgb, var(--err) 14%, transparent)',
    border: '1px solid #d9534f', borderRadius: 7, padding: '7px 9px', cursor: 'pointer' },
  hint: { fontSize: 10.5, color: T.dim, lineHeight: 1.5 },
  empty: { display: 'flex', flexDirection: 'column', gap: 6, padding: 14,
    border: `1px solid ${T.border}`, borderRadius: 9 },
  emptyHead: { fontSize: 13, fontWeight: 600, color: T.text },
};
